import React, { useState, useEffect, useContext } from 'react';

import { signIn, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import Footer from '../../../shared/components/v3/Footer/Footer';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { LOGIN_PROVIDER_MICROSOFT, LOGIN_ROUTE } from '../../../shared/constants/auth';
import { MICROSOFT_PROVIDER } from '../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../shared/constants/toast';
import { SessionContext } from '../../../shared/context/auth/authContext';
import Users from '../../../shared/services/api/users/users';
import { generatePassword } from '../../../shared/utils/auth/auth';
import VerificationCode from '../sections/VerificationCode/VerificationCode';

const CreateMicrosoftAccount = () => {
  const [verificationFailed, setVerificationFailed] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState(false);
  const [loading, setLoading] = useState(false);

  const { customLogin, errorCode, resetError } = useContext(SessionContext);
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { email?: string; idToken?: string } | null;
  const email = state?.email || '';
  const idToken = state?.idToken || '';

  const { t } = useTranslation();

  useEffect(() => {
    if (!email || !idToken) {
      navigate(LOGIN_ROUTE, { replace: true });
    }
  }, [email, idToken, navigate]);

  useEffect(() => {
    if (errorCode) {
      setOpenToast(true);
      resetError('');
    }
  }, [errorCode, resetError]);

  const handleLogin = async (idToken: string, email: string) => {
    try {
      setLoading(true);
      const signInResult = await signIn({ username: email, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        await confirmSignIn({ challengeResponse: idToken });
        const session = await fetchAuthSession();
        const cognitoIdToken = session.tokens?.idToken;
        let adminEmail = '';
        let userId = '';
        if (cognitoIdToken && typeof cognitoIdToken === 'object' && cognitoIdToken.payload) {
          adminEmail = typeof cognitoIdToken.payload.email === 'string' ? cognitoIdToken.payload.email : '';
          userId = typeof cognitoIdToken.payload.sub === 'string' ? cognitoIdToken.payload.sub : '';
        }
        await customLogin(
          { attributes: { email: adminEmail, sub: userId } },
          LOGIN_PROVIDER_MICROSOFT,
          MICROSOFT_PROVIDER
        );
      }
    } catch {
      resetError('custom sign in error');
      navigate(LOGIN_ROUTE);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCode = async (code: string) => {
    try {
      setLoading(true);
      setVerificationFailed(false);
      const response = await Users.verifyEmail(email, code);
      const password = generatePassword(99);
      await Users.signUpMicrosoft(email, password, LOGIN_PROVIDER_MICROSOFT, idToken, response.token);
      await handleLogin(idToken, email);
    } catch {
      setOpenToast(true);
      setVerificationFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-container" data-experience="new">
        <VerificationCode
          codeType="create-account"
          email={email}
          loading={loading}
          hasError={verificationFailed}
          handleNext={handleVerificationCode}
          handleGoBack={() => navigate(LOGIN_ROUTE)}
        />
        <Toast
          title={t(GENERIC_ERROR_TOAST.title)}
          description={t(GENERIC_ERROR_TOAST.description)}
          icon={GENERIC_ERROR_TOAST.icon}
          open={openToast}
          button={true}
          handleClose={() => setOpenToast(false)}
        />
      </div>
      <Footer login={true} />
    </>
  );
};

export default CreateMicrosoftAccount;
